import { Patient, Practice, Professional } from '@aldebaran/prisma';
import {
  ResponseLevel,
  StructuredQuestionnaireData,
} from '@aldebaran/common/types/questionnaire';
import {
  formatAsUtc,
  formatTimeOfDay,
  isValidDate,
  isValidDuration,
} from '@aldebaran/common/util/date';
import {
  formatStructuredValue,
  isStructuredValue,
} from '@aldebaran/common/util/structuredValue';
import { format } from 'date-fns';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import without from 'lodash/without';

import {
  ConsultationState,
  FormulaStore,
  GraphState,
  KnowledgeBaseState,
  QuestionResponseStore,
  QuestionnaireConfig,
  QuestionnaireState,
  QuestionnaireSummary,
} from '../types/graph';
import {
  ConsultationMotive,
  Explanation,
  Report,
  Unit,
  Value,
} from '../types/global';
import { checkCondition, evaluate } from './graph';
import {
  getAppointmentServiceName,
  getFormalName,
  getFormalNameWithArticle,
} from './professional';

type ProcessLabelContext = {
  patient: Patient | null | undefined;
  professional: Professional | null | undefined;
  practice: Practice | null | undefined;
};

type StandaloneExplanation = Omit<Explanation, 'condition'> & {
  condition?: string;
};

type StandaloneReport = Omit<Report, 'explanations'> & {
  explanations?: StandaloneExplanation[] | null;
};

type TransformReportsParams = {
  reports: (Report | StandaloneReport)[] | null | undefined;
  userValue?: Value;
  unit?: Unit;
  format?: string;
  checkConditionFn: (condition: string | undefined) => boolean;
  evaluateFn: (clause: string) => Value;
  processLabelFn: (label: string) => string;
};

const excludedQuestions = ['CONSULTATION_DATE', 'CONSULTATION_TIME'];

const UTC_DATE_FORMATS: Record<string, string> = {
  DATE_OF_BIRTH: 'dd/MM/yyyy',
};

const DATE_FORMATS: Record<string, string> = {
  DATE: 'dd/MM/yyyy',
  DATETIME: 'dd/MM/yyyy HH:mm',
  TIME: 'HH:mm',
};

const hasAnswer = (value: Value | undefined): value is Value =>
  value != null && value !== false && value !== '';

export const formatRawValue = (
  value: Value,
  responseFormat: string,
): string => {
  if (isStructuredValue(value)) return formatStructuredValue(value);
  if (typeof value !== 'number') return String(value).trim();

  if (responseFormat === 'DURATION') {
    return isValidDuration(value) ? formatTimeOfDay(value) : String(value);
  }

  const utcFormat = UTC_DATE_FORMATS[responseFormat];
  if (utcFormat && isValidDate(value)) {
    return formatAsUtc(value, utcFormat);
  }

  const dateFormat = DATE_FORMATS[responseFormat];
  if (dateFormat && isValidDate(value)) {
    return format(value, dateFormat);
  }

  return String(value).trim();
};

const formatWithUnit = (
  value: Value | undefined,
  unit: Unit | undefined,
): string | null => {
  if (value == null || !unit?.plural) return null;
  const unitLabel =
    value === 0 || value === 1 ? unit.singular ?? unit.plural : unit.plural;
  return `${value} ${unitLabel}`;
};

const formatAnswerText = (
  value: Value,
  response: { unit?: Unit; format: string },
): string =>
  isStructuredValue(value)
    ? formatStructuredValue(value)
    : formatWithUnit(value, response.unit) ??
      formatRawValue(value, response.format);

/**
 * Capitalizes a substituted value when it opens a sentence — after `.`, `!` or
 * `?`, and at the very start of the label, where nothing precedes it.
 *
 * The start-of-label case is what response labels reduced to a single variable
 * need (`->[{{PROFESSIONAL_NAME_WITH_ARTICLE}}]`): the list item *is* the name,
 * so nothing can carry the capital for it, and it is read next to siblings that
 * all have one ("Un autre médecin", "Je ne sais pas").
 */
const capitalizeIfStartOfSentence = (
  precedingText: string,
  text: string,
): string => {
  const preceding = precedingText.trim();

  return preceding === '' || /[.!?]/.test(preceding.slice(-1))
    ? text.charAt(0).toUpperCase() + text.slice(1)
    : text;
};

/**
 * Stands in for the professional's name when none is resolved, or when the
 * practice sets `disableProfessionalNameDisplayInQuestionnaire`. It replaces
 * the whole token, article included, which is why the article belongs to
 * `{{PROFESSIONAL_NAME_WITH_ARTICLE}}` and never to the label.
 */
const ANONYMOUS_PROFESSIONAL = 'ce médecin';

export const processLabel = (
  label: string,
  context: ProcessLabelContext,
): string => {
  const { patient, professional, practice } = context;

  const showProfessionalName =
    professional != null &&
    !practice?.disableProfessionalNameDisplayInQuestionnaire;

  const templates: Record<string, string> = {
    '{{USER_FIRST_NAME}}': patient?.firstName ?? 'vous',
    '{{PATIENT_FIRST_NAME}}': patient?.firstName ?? 'vous',
    // Mid-sentence: "… rendez-vous avec {{PROFESSIONAL_NAME_WITH_ARTICLE}} ?"
    // → "avec le Dr Dupont" / "avec ce médecin".
    '{{PROFESSIONAL_NAME_WITH_ARTICLE}}': showProfessionalName
      ? getFormalNameWithArticle(professional)
      : ANONYMOUS_PROFESSIONAL,
    // Standalone, where an article would be wrong (report header, a label
    // reduced to the name) → "Dr Dupont" / "ce médecin".
    '{{PROFESSIONAL_NAME}}': showProfessionalName
      ? getFormalName(professional)
      : ANONYMOUS_PROFESSIONAL,
    '{{APPOINTMENT_SERVICE}}': getAppointmentServiceName(professional),
  };

  return label
    ? Object.keys(templates).reduce((acc, template) => {
        return acc.replaceAll(template, (_, offset, string) => {
          const precedingText = string.slice(0, offset);
          const replacement = templates[template];

          return capitalizeIfStartOfSentence(precedingText, replacement);
        });
      }, label)
    : label;
};

export const computeQuestionnaireSummary = ({
  userResponses,
  kbQuestions,
  sectionAssignments,
  sections,
}: {
  userResponses: QuestionResponseStore;
  kbQuestions: KnowledgeBaseState['questions'];
  sectionAssignments: QuestionnaireState['sectionAssignments'];
  sections: string[];
}): QuestionnaireSummary => {
  return without(
    Object.keys(userResponses),
    ...excludedQuestions,
  ).reduce<QuestionnaireSummary>((acc, extId) => {
    const question = kbQuestions[extId];

    if (!question?.label) return acc;

    if (
      (question.section && !sections.includes(question.section)) ||
      (!question.section && !sectionAssignments[question.extId])
    ) {
      return acc;
    }

    acc[extId] = {
      question: question.label,
      responses: question.responses
        .filter((response) =>
          hasAnswer(userResponses?.[question.extId]?.[response.extId]),
        )
        .map((response) => {
          const value = userResponses[question.extId][response.extId];
          if (value === true) return response.label;
          return formatAnswerText(value, response);
        }),
    };

    if (acc[extId].responses.length === 0) {
      acc[extId].responses = ['Non exploré'];
    }

    return acc;
  }, {});
};

export const computeStructuredQuestionnaireData = ({
  userResponses,
  kbQuestions,
  kbResponses,
  totalQueue,
  activeQueue,
  currentPage,
  config,
}: {
  userResponses: QuestionResponseStore;
  kbQuestions: KnowledgeBaseState['questions'];
  kbResponses: KnowledgeBaseState['responses'];
  totalQueue: QuestionnaireState['totalQueue'];
  activeQueue: string[];
  currentPage: string;
  config: QuestionnaireConfig | null | undefined;
}): StructuredQuestionnaireData => {
  const buildEntry = (questionId: string, pageId: string) => {
    if (excludedQuestions.includes(questionId)) return null;

    const question = kbQuestions[questionId];
    if (!question?.label) return null;

    const questionUserResponses = userResponses[questionId];
    if (
      !questionUserResponses ||
      Object.keys(questionUserResponses).length === 0
    ) {
      return null;
    }

    const structuredResponses = (question.responses ?? [])
      .map((responseRef) => {
        const response = kbResponses[responseRef.extId];
        if (!response) return null;

        const value = questionUserResponses[responseRef.extId];
        const chosenByPatient = hasAnswer(value);

        const text =
          chosenByPatient && value !== true
            ? formatAnswerText(value, response)
            : response.label;

        return {
          id: responseRef.extId,
          text,
          value: chosenByPatient ? value : false,
          level: response.level as ResponseLevel | undefined,
          chosenByPatient,
        };
      })
      .filter((response): response is NonNullable<typeof response> => {
        return response !== null;
      });

    if (structuredResponses.length === 0) return null;

    return {
      questionId,
      questionText: question.label,
      pageId,
      pageTitle: config?.pages?.[pageId]?.title ?? '',
      responses: structuredResponses,
    };
  };

  const result: StructuredQuestionnaireData = [];
  const seen = new Set<string>();

  totalQueue.forEach((pageQueue) => {
    const pageId = Object.keys(pageQueue)[0];

    (pageQueue[pageId] || []).forEach((questionId) => {
      if (questionId && !seen.has(questionId)) {
        seen.add(questionId);
        const entry = buildEntry(questionId, pageId);
        if (entry) result.push(entry);
      }
    });
  });

  activeQueue.forEach((questionId) => {
    if (questionId && !seen.has(questionId)) {
      seen.add(questionId);
      const entry = buildEntry(questionId, currentPage);
      if (entry) result.push(entry);
    }
  });

  return result;
};

const transformReports = ({
  reports,
  userValue,
  unit,
  format,
  checkConditionFn,
  evaluateFn,
  processLabelFn,
}: TransformReportsParams): Report[] => {
  if (reports == null || !reports.length) return [];

  return reports
    .filter((report) => checkConditionFn(report.condition))
    .map((report) => {
      const [reportValue, clause] = report.value.split('-<EVALUE>->');
      const graphValue = clause
        ? evaluateFn(clause)?.toString()
        : userValue != null && format
        ? formatAnswerText(userValue, { unit, format })
        : userValue?.toString().trim();

      const filteredExplanations = report.explanations?.filter(
        (explanation) =>
          !explanation.condition || checkConditionFn(explanation.condition),
      );
      const activeExplanations = filteredExplanations?.length
        ? filteredExplanations.map((explanation) => ({
            ...explanation,
            condition: explanation.condition ?? '',
          }))
        : undefined;

      return {
        ...report,
        value: processLabelFn(reportValue.replace('#?', graphValue ?? '')),
        explanations: activeExplanations,
      };
    });
};

export const computeReport = ({
  userResponses,
  kbResponses,
  graph,
  formulas,
  standaloneReports,
  consultation,
}: {
  userResponses: QuestionResponseStore;
  kbResponses: KnowledgeBaseState['responses'];
  graph: GraphState;
  formulas: FormulaStore;
  standaloneReports: StandaloneReport[] | undefined;
  consultation: ConsultationState;
}): Report[] => {
  const checkConditionFn = (condition: string | undefined) => {
    return checkCondition(graph, formulas, condition);
  };
  const evaluateFn = (clause: string) =>
    evaluate(graph.values, formulas, clause);
  const processLabelFn = (label: string) =>
    processLabel(label, {
      patient: consultation.patient,
      professional: consultation.professional,
      practice: consultation.practice,
    });

  const transformedReports = Object.values(userResponses).flatMap(
    (responseMap) =>
      Object.entries(responseMap).flatMap(([extId, userValue]) => {
        if (hasAnswer(userValue) && kbResponses[extId]) {
          return transformReports({
            reports: kbResponses[extId].reports,
            userValue,
            unit: kbResponses[extId].unit,
            format: kbResponses[extId].format,
            checkConditionFn,
            evaluateFn,
            processLabelFn,
          });
        }

        return [];
      }),
  );

  const transformedStandaloneReports = transformReports({
    reports: standaloneReports,
    checkConditionFn,
    evaluateFn,
    processLabelFn,
  });

  return sortBy(
    uniqBy([...transformedReports, ...transformedStandaloneReports], 'id'),
    'classification',
  );
};

export const computeConsultationMotives = ({
  report,
  motivesList,
}: {
  report: Report[];
  motivesList: ConsultationMotive[];
}): ConsultationMotive['id'][] => {
  return report
    .map(
      (reportItem) =>
        motivesList.find(
          (motive) => motive.kbId && motive.kbId === reportItem.classification,
        )?.id,
    )
    .filter(
      (motive): motive is ConsultationMotive['id'] => motive !== undefined,
    );
};
